/**
 * TeenPattiAI - AI opponents for Teen Patti
 *
 * Three difficulty levels:
 *   Easy:   Random play, slight hand awareness, no sideshows, 500-1500ms delay
 *   Medium: Hand strength + pot odds, 15% bluff rate, strategic sideshows, 1000-2500ms
 *   Hard:   Bluff detection, opponent tracking, pot odds, strategic Blind/Seen, 1500-3000ms
 */

import {
  TeenPattiHandRank,
  TeenPattiAction, ValidTeenPattiAction, TeenPattiDifficulty,
  VisibleTeenPattiState, TeenPattiActionRecord,
} from './types';
import * as HandEvaluator from './TeenPattiHandEvaluator';

// ============================================================================
// Opponent Tracker (Hard AI)
// ============================================================================

interface OpponentProfile {
  handsObserved: number;
  raiseCount: number;
  callCount: number;
  packCount: number;
  blindRoundsTotal: number;
  sideshowRequests: number;
}

class OpponentTracker {
  private profiles: Map<string, OpponentProfile> = new Map();

  getProfile(playerId: string): OpponentProfile {
    if (!this.profiles.has(playerId)) {
      this.profiles.set(playerId, {
        handsObserved: 0, raiseCount: 0, callCount: 0,
        packCount: 0, blindRoundsTotal: 0, sideshowRequests: 0,
      });
    }
    return this.profiles.get(playerId)!;
  }

  recordAction(action: TeenPattiActionRecord): void {
    const p = this.getProfile(action.playerId);
    if (action.action === 'raise') p.raiseCount++;
    if (action.action === 'call') p.callCount++;
    if (action.action === 'pack') p.packCount++;
    if (action.wasBlind) p.blindRoundsTotal++;
  }

  recordNewHand(playerIds: string[]): void {
    for (const id of playerIds) {
      this.getProfile(id).handsObserved++;
    }
  }

  getAggressionFactor(playerId: string): number {
    const p = this.getProfile(playerId);
    const total = p.raiseCount + p.callCount;
    return total > 0 ? p.raiseCount / total : 0.3;
  }

  getPackRate(playerId: string): number {
    const p = this.getProfile(playerId);
    const total = p.raiseCount + p.callCount + p.packCount;
    return total > 0 ? p.packCount / total : 0.3;
  }

  reset(): void { this.profiles.clear(); }
}

// ============================================================================
// Helper functions
// ============================================================================

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function findAction(validActions: ValidTeenPattiAction[], type: string): ValidTeenPattiAction | undefined {
  return validActions.find(a => a.type === type);
}

function hasAction(validActions: ValidTeenPattiAction[], type: string): boolean {
  return validActions.some(a => a.type === type);
}

// ============================================================================
// TeenPattiAI Class
// ============================================================================

export class TeenPattiAI {
  difficulty: TeenPattiDifficulty;
  private opponentTracker: OpponentTracker;
  private blindRoundsPlayed: Map<string, number> = new Map();
  testMode: boolean = false;

  constructor(difficulty: TeenPattiDifficulty = 'easy') {
    this.difficulty = difficulty;
    this.opponentTracker = new OpponentTracker();
  }

  async getAction(
    gameState: VisibleTeenPattiState,
    playerId: string,
    validActions: ValidTeenPattiAction[]
  ): Promise<TeenPattiAction> {
    if (validActions.length === 0) return { type: 'pack' };
    if (validActions.length === 1) return { type: validActions[0].type };

    if (!this.testMode) {
      const delayMs = this.getThinkingDelay();
      await delay(delayMs);
    }

    // Track actions from history
    for (const action of gameState.actionHistory) {
      this.opponentTracker.recordAction(action);
    }

    switch (this.difficulty) {
      case 'easy': return this.getEasyAction(gameState, playerId, validActions);
      case 'medium': return this.getMediumAction(gameState, playerId, validActions);
      case 'hard': return this.getHardAction(gameState, playerId, validActions);
      default: return this.getEasyAction(gameState, playerId, validActions);
    }
  }

  async respondToSideshow(
    gameState: VisibleTeenPattiState,
    playerId: string,
    _requesterId: string
  ): Promise<boolean> {
    if (!this.testMode) await delay(randomInRange(500, 1500));
    const player = gameState.players.find(p => p.id === playerId);
    if (!player || !player.cards) return false;
    const hand = HandEvaluator.evaluate(player.cards);
    // Accept if hand is decent
    if (this.difficulty === 'easy') return true;
    if (this.difficulty === 'medium') return hand.rank >= TeenPattiHandRank.PAIR;
    // Hard: accept if strong, decline if weak to avoid revealing info
    return hand.rank >= TeenPattiHandRank.SEQUENCE;
  }

  setDifficulty(difficulty: TeenPattiDifficulty): void {
    this.difficulty = difficulty;
  }

  reset(): void {
    this.opponentTracker.reset();
    this.blindRoundsPlayed.clear();
  }

  // =========================================================================
  // Easy AI
  // =========================================================================

  private getEasyAction(
    gameState: VisibleTeenPattiState,
    playerId: string,
    validActions: ValidTeenPattiAction[]
  ): TeenPattiAction {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return { type: 'pack' };

    const blindRounds = this.blindRoundsPlayed.get(playerId) || 0;
    const isBlind = player.blindSeenStatus === 'blind';

    // Stay blind for 2-3 rounds
    if (isBlind && blindRounds < Math.floor(randomInRange(2, 4))) {
      this.blindRoundsPlayed.set(playerId, blindRounds + 1);
      // Call as blind (low stakes)
      if (hasAction(validActions, 'call')) return { type: 'call' };
      return { type: 'pack' };
    }

    // See cards if still blind
    if (isBlind && hasAction(validActions, 'see-cards')) {
      this.blindRoundsPlayed.set(playerId, 0);
      return { type: 'see-cards' };
    }

    // After seeing cards, evaluate hand
    if (player.cards) {
      const hand = HandEvaluator.evaluate(player.cards);
      if (hand.rank >= TeenPattiHandRank.SEQUENCE) {
        const raiseAction = findAction(validActions, 'raise');
        if (raiseAction && raiseAction.minAmount) {
          return { type: 'raise', amount: raiseAction.minAmount };
        }
      }
      if (hand.rank >= TeenPattiHandRank.PAIR) {
        if (hasAction(validActions, 'call')) return { type: 'call' };
      }
      // Weak high card - pack if card is low
      if (hand.rank === TeenPattiHandRank.HIGH_CARD) {
        const highValue = hand.comparisonKey[1] || 0;
        if (highValue < 8) return { type: 'pack' };
      }
    }

    if (hasAction(validActions, 'call')) return { type: 'call' };
    if (hasAction(validActions, 'show')) return { type: 'show' };
    return { type: 'pack' };
  }

  // =========================================================================
  // Medium AI
  // =========================================================================

  private getMediumAction(
    gameState: VisibleTeenPattiState,
    playerId: string,
    validActions: ValidTeenPattiAction[]
  ): TeenPattiAction {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return { type: 'pack' };

    const isBlind = player.blindSeenStatus === 'blind';
    const blindRounds = this.blindRoundsPlayed.get(playerId) || 0;
    const bluffRoll = Math.random();
    const potOdds = this.calculatePotOdds(gameState, validActions);

    // Blind/Seen decision
    if (isBlind && blindRounds > 3 && hasAction(validActions, 'see-cards')) {
      this.blindRoundsPlayed.set(playerId, 0);
      return { type: 'see-cards' };
    }
    if (isBlind) {
      this.blindRoundsPlayed.set(playerId, blindRounds + 1);
      if (hasAction(validActions, 'call')) return { type: 'call' };
      return { type: 'pack' };
    }

    if (!player.cards) {
      if (hasAction(validActions, 'call')) return { type: 'call' };
      return { type: 'pack' };
    }

    const hand = HandEvaluator.evaluate(player.cards);

    if (hand.rank >= TeenPattiHandRank.TRAIL) {
      const raiseAction = findAction(validActions, 'raise');
      if (raiseAction && raiseAction.maxAmount) {
        return { type: 'raise', amount: raiseAction.maxAmount };
      }
    }

    if (hand.rank >= TeenPattiHandRank.SEQUENCE && potOdds < 0.4) {
      const raiseAction = findAction(validActions, 'raise');
      if (raiseAction && raiseAction.minAmount && raiseAction.maxAmount) {
        const mid = Math.floor((raiseAction.minAmount + raiseAction.maxAmount) / 2);
        return { type: 'raise', amount: mid };
      }
    }

    if (hand.rank >= TeenPattiHandRank.PAIR) {
      if (hasAction(validActions, 'call')) return { type: 'call' };
    }

    // Bluff ~15%
    if (bluffRoll < 0.15) {
      const raiseAction = findAction(validActions, 'raise');
      if (raiseAction && raiseAction.minAmount) {
        return { type: 'raise', amount: raiseAction.minAmount };
      }
    }

    // Sideshow with moderate hands
    if (hand.rank >= TeenPattiHandRank.PAIR && hasAction(validActions, 'sideshow')) {
      return { type: 'sideshow' };
    }

    if (potOdds > 0.5) return { type: 'pack' };
    if (hasAction(validActions, 'call')) return { type: 'call' };
    if (hasAction(validActions, 'show')) return { type: 'show' };
    return { type: 'pack' };
  }

  // =========================================================================
  // Hard AI
  // =========================================================================

  private getHardAction(
    gameState: VisibleTeenPattiState,
    playerId: string,
    validActions: ValidTeenPattiAction[]
  ): TeenPattiAction {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return { type: 'pack' };

    const isBlind = player.blindSeenStatus === 'blind';
    const blindRounds = this.blindRoundsPlayed.get(playerId) || 0;
    const potOdds = this.calculatePotOdds(gameState, validActions);

    // Strategic Blind/Seen: vary to be unpredictable
    if (isBlind) {
      const shouldStayBlind = Math.random() < 0.6 && blindRounds < 4;
      if (shouldStayBlind) {
        this.blindRoundsPlayed.set(playerId, blindRounds + 1);
        // Occasionally raise blind to confuse opponents
        if (Math.random() < 0.2) {
          const raiseAction = findAction(validActions, 'raise');
          if (raiseAction && raiseAction.minAmount) {
            return { type: 'raise', amount: raiseAction.minAmount };
          }
        }
        if (hasAction(validActions, 'call')) return { type: 'call' };
        return { type: 'pack' };
      }
      if (hasAction(validActions, 'see-cards')) {
        this.blindRoundsPlayed.set(playerId, 0);
        return { type: 'see-cards' };
      }
    }

    if (!player.cards) {
      if (hasAction(validActions, 'call')) return { type: 'call' };
      return { type: 'pack' };
    }

    const hand = HandEvaluator.evaluate(player.cards);

    // Analyze opponents
    const opponents = gameState.players.filter(p => p.id !== playerId && p.status === 'active');
    const avgAggression = opponents.reduce((sum, o) =>
      sum + this.opponentTracker.getAggressionFactor(o.id), 0) / Math.max(opponents.length, 1);
    const avgPackRate = opponents.reduce((sum, o) =>
      sum + this.opponentTracker.getPackRate(o.id), 0) / Math.max(opponents.length, 1);

    // Premium hands
    if (hand.rank >= TeenPattiHandRank.TRAIL) {
      const raiseAction = findAction(validActions, 'raise');
      if (raiseAction && raiseAction.maxAmount) {
        // Vary sizing to avoid being predictable
        const amount = Math.floor(randomInRange(
          raiseAction.maxAmount * 0.7, raiseAction.maxAmount
        ));
        return { type: 'raise', amount };
      }
    }

    if (hand.rank >= TeenPattiHandRank.SEQUENCE) {
      const raiseAction = findAction(validActions, 'raise');
      if (raiseAction && raiseAction.minAmount && raiseAction.maxAmount) {
        // Raise more against passive opponents
        const sizeFactor = avgAggression < 0.3 ? 0.8 : 0.5;
        const amount = Math.floor(raiseAction.minAmount + (raiseAction.maxAmount - raiseAction.minAmount) * sizeFactor);
        return { type: 'raise', amount };
      }
      if (hasAction(validActions, 'call')) return { type: 'call' };
    }

    // Bluff detection: if opponent raises frequently, they might be bluffing
    if (hand.rank >= TeenPattiHandRank.PAIR && avgAggression > 0.5) {
      if (hasAction(validActions, 'call')) return { type: 'call' };
    }

    // Strategic sideshow
    if (hand.rank >= TeenPattiHandRank.COLOR && hasAction(validActions, 'sideshow')) {
      if (avgPackRate > 0.4) return { type: 'sideshow' };
    }

    // Balanced bluffing
    const bluffFreq = avgPackRate > 0.4 ? 0.25 : 0.1;
    if (Math.random() < bluffFreq && hand.rank < TeenPattiHandRank.PAIR) {
      const raiseAction = findAction(validActions, 'raise');
      if (raiseAction && raiseAction.minAmount) {
        return { type: 'raise', amount: raiseAction.minAmount };
      }
    }

    if (potOdds < 0.3 && hasAction(validActions, 'call')) return { type: 'call' };
    if (hasAction(validActions, 'show')) return { type: 'show' };
    if (hasAction(validActions, 'call') && potOdds < 0.5) return { type: 'call' };
    return { type: 'pack' };
  }

  // =========================================================================
  // Utility
  // =========================================================================

  private calculatePotOdds(
    gameState: VisibleTeenPattiState,
    validActions: ValidTeenPattiAction[]
  ): number {
    const callAction = findAction(validActions, 'call');
    const amountToCall = callAction?.callAmount || gameState.currentStake;
    const pot = gameState.pot;
    return amountToCall / (pot + amountToCall);
  }

  private getThinkingDelay(): number {
    switch (this.difficulty) {
      case 'easy': return randomInRange(500, 1500);
      case 'medium': return randomInRange(1000, 2500);
      case 'hard': return randomInRange(1500, 3000);
      default: return randomInRange(500, 1500);
    }
  }
}
